import { formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AlertCircle, Clock, Check, Ban, MoreVertical, History, Undo2 } from "lucide-react";

export const datasVenda = (i) => (i.vendas?.length ? i.vendas.map((v) => v.data) : [i.data]);

function Tag({ cls, children, testid, title }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase w-fit ${cls}`} data-testid={testid} title={title}>
      {children}
    </span>
  );
}

function StatusPrincipal({ i, st }) {
  if (st === "pendente") return <Tag cls="bg-amber-100 text-amber-800 text-xs normal-case"><Clock className="w-3 h-3" /> Pendente</Tag>;
  if (st === "emitida") return <Tag cls="bg-emerald-100 text-emerald-800 text-xs normal-case"><Check className="w-3 h-3" /> Emitida</Tag>;
  return (
    <>
      <Tag cls="bg-slate-200 text-slate-700 text-xs normal-case" testid={`badge-cancelada-${i.id}`}>
        <Ban className="w-3 h-3" /> Cancelada{i.nf_cancel_tipo === "nf_emitida" ? " (NF emitida)" : ""}
      </Tag>
      <div className="text-[10px] text-slate-500 leading-tight max-w-[200px]" data-testid={`cancel-info-${i.id}`}>
        {i.nf_cancel_motivo}
        <br />{i.nf_cancelada_por_nome} · {formatDateTime(i.nf_cancelada_em)}
        {i.nf_cancel_protocolo ? <><br />Protocolo {i.nf_cancel_protocolo}</> : null}
      </div>
    </>
  );
}

export function NfStatusCell({ i }) {
  const st = i.nf_status || "pendente";
  const ativa = st !== "cancelada";
  const pedido = i.nf_cancelamento_pedido;
  const ultimaRev = (i.nf_revisoes || []).slice(-1)[0];
  return (
    <div className="flex flex-col gap-1">
      {st === "emitida" && i.alterada_apos_emissao && (
        <Tag cls="bg-red-500 text-white" testid={`badge-alterada-${i.id}`}><AlertCircle className="w-3 h-3" /> Alterada — revisar</Tag>
      )}
      {ativa && i.nf_precisa_revisao && (
        <Tag cls="bg-red-500 text-white" testid={`badge-revisao-${i.id}`}><AlertCircle className="w-3 h-3" /> Revisão pedida</Tag>
      )}
      {ativa && i.nf_precisa_revisao && ultimaRev && (
        <div className="text-[10px] text-red-700 leading-tight max-w-[220px] line-clamp-2" title={ultimaRev.observacao} data-testid={`revisao-texto-${i.id}`}>
          “{ultimaRev.observacao}” · {ultimaRev.solicitante_nome}
        </div>
      )}
      {ativa && pedido && (
        <Tag cls="bg-orange-500 text-white" testid={`badge-cancel-pedido-${i.id}`} title={`${pedido.por_nome}: ${pedido.motivo}`}>
          <Ban className="w-3 h-3" /> Cancelamento pedido
        </Tag>
      )}
      {ativa && i.nf_substituir?.length > 0 && (
        <Tag cls="bg-red-600 text-white normal-case" testid={`badge-substituir-${i.id}`} title="Inscrição unificada: cancele a NF antiga e emita a nova com todos">
          <AlertCircle className="w-3 h-3" /> Cancelar NF nº {i.nf_substituir.map((s) => s.nf_numero).join(", ")} e emitir a nova
        </Tag>
      )}
      <StatusPrincipal i={i} st={st} />
    </div>
  );
}

export function NfAcoesMenu({ i, onHistorico, onCancelar, onDesfazer }) {
  const st = i.nf_status || "pendente";
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`btn-acoes-${i.id}`}>
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => onHistorico(i)} data-testid={`acao-historico-${i.id}`}>
          <History className="w-4 h-4 mr-2" /> Ver histórico
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {st === "pendente" && (
          <DropdownMenuItem onSelect={() => onCancelar(i)} className="text-red-600" data-testid={`acao-cancelar-${i.id}`}>
            <Ban className="w-4 h-4 mr-2" /> Cancelar solicitação
          </DropdownMenuItem>
        )}
        {st === "emitida" && (
          <DropdownMenuItem onSelect={() => onCancelar(i)} className="text-red-600" data-testid={`acao-registrar-cancel-${i.id}`}>
            <Ban className="w-4 h-4 mr-2" /> Registrar cancelamento da NF
          </DropdownMenuItem>
        )}
        {st === "cancelada" && i.nf_cancel_tipo === "solicitacao" && (
          <DropdownMenuItem onSelect={() => onDesfazer(i)} data-testid={`acao-desfazer-${i.id}`}>
            <Undo2 className="w-4 h-4 mr-2" /> Desfazer cancelamento
          </DropdownMenuItem>
        )}
        {st === "cancelada" && i.nf_cancel_tipo !== "solicitacao" && (
          <DropdownMenuItem disabled>Nota cancelada no emissor</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
